import {
  BOLD_FONT,
  ITALIC_FONT,
  type Line,
  SAME_LINE_TOLERANCE,
} from './text-layout'

const SPACE_GAP_RATIO = 0.15

/** The parts of a pdf.js `TextItem` that the layout needs. */
export type PdfTextItem = {
  str: string
  transform: number[]
  width: number
  fontName: string
}

export type PdfPageText = {
  items: PdfTextItem[]
  /** pdf.js font id → the PDF's own font name, e.g. `ABCDEF+Aptos-Bold`. */
  fontNames: Map<string, string>
}

/**
 * Groups pdf.js text items into visual lines. pdf.js gives each item's
 * baseline in PDF space, where y grows upwards, and splits a line wherever the
 * font changes - often without the space between the words, so one is put
 * back when the items are visibly apart.
 */
export function pdfTextLines(pages: PdfPageText[]): Line[] {
  const lines: Line[] = []

  pages.forEach(({ items, fontNames }) => {
    const page = {}
    let lineEnd = 0

    for (const item of items) {
      if (!item.str) continue

      const [, , skew = 0, scale = 0, x = 0, baseline = 0] = item.transform
      const size = Math.round(Math.hypot(skew, scale) * 10) / 10
      const y = -baseline
      const font = fontNames.get(item.fontName) ?? ''
      const run = {
        text: item.str,
        bold: BOLD_FONT.test(font),
        italic: ITALIC_FONT.test(font),
      }
      const last = lines[lines.length - 1]

      if (last?.page === page && Math.abs(last.y - y) < SAME_LINE_TOLERANCE) {
        const previous = last.runs[last.runs.length - 1]
        const apart = x - lineEnd > size * SPACE_GAP_RATIO
        if (
          apart &&
          !/\s$/.test(previous?.text ?? ' ') &&
          !/^\s/.test(run.text)
        )
          last.runs.push({ text: ' ', bold: false, italic: false })

        last.runs.push(run)
        last.size = Math.max(last.size, size)
      } else lines.push({ page, y, size, runs: [run] })

      lineEnd = x + item.width
    }
  })
  return lines
}
